import { GeoJSON } from 'react-leaflet';

interface DrawnLayersProps {
  geojson: GeoJSON.FeatureCollection | null;
  socketRef?: React.MutableRefObject<WebSocket | null>;
  usernameCache?: Map<string, string>;
  onRegisterPendingRequest?: (requestId: string, data: any) => void;
  onLayerEdited?: (layerData: any) => void;
  onLayerDeleted?: (layerId: string) => void;
}

export function DrawnLayers({ geojson, socketRef, usernameCache = new Map(), onRegisterPendingRequest, onLayerEdited, onLayerDeleted }: DrawnLayersProps) {
  if (!geojson || !geojson.features || geojson.features.length === 0) {
    return null;
  }

  // Function to query username from server
  const fetchUsername = (userId: string, layer: any) => {
    // Check cache first
    if (usernameCache.has(userId)) {
      const username = usernameCache.get(userId);
      layer.bindPopup(`Created by: ${username}`).openPopup();
      return;
    }

    // Generate a unique request ID
    const requestId = `${Date.now()}_${Math.random()}`;
    
    // Register the pending request
    if (onRegisterPendingRequest) {
      onRegisterPendingRequest(requestId, { layer, userId });
    }

    // Send query to server
    if (socketRef?.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'query_username',
        userId: userId,
        requestId: requestId
      }));
      
      // Show loading popup
      layer.bindPopup('Loading username...').openPopup();
    } else {
      // Fallback if no connection
      layer.bindPopup(`Created by: User_${userId.substring(0, 8)}`).openPopup();
    }
  };

  // Style function to apply color from feature properties
  const style = (feature: any) => {
    return {
      color: feature.properties?.color || '#3388ff',
      weight: 2,
      opacity: 1,
      fillOpacity: 0.2
    };
  };

  // Handle feature clicks and set initial popup
  const onEachFeature = (feature: any, layer: any) => {
    // Set initial popup content based on cache
    if (feature.properties?.userId) {
      const cachedUsername = usernameCache.get(feature.properties.userId);
      if (cachedUsername) {
        layer.bindPopup(`Created by: ${cachedUsername}`);
      } else {
        layer.bindPopup('Click to see creator');
      }
    } else if (feature.properties?.createdBy) {
      layer.bindPopup(`Created by: ${feature.properties.createdBy}`);
    }
    
    layer.on({
      click: () => {
        if (feature.properties?.userId) {
          fetchUsername(feature.properties.userId, layer);
        } else if (feature.properties?.createdBy) {
          // Fallback to cached username if available
          layer.openPopup();
        }
      },
      'pm:edit': () => {
        if (onLayerEdited) {
          setTimeout(() => {
            onLayerEdited(layer.toGeoJSON());
          }, 0);
        }
      },
      'pm:remove': () => {
        if (onLayerDeleted && feature.properties?.id) {
          setTimeout(() => {
            onLayerDeleted(feature.properties.id);
          }, 0);
        }
      }
    });
  };

  // Create a key that includes geometry coordinates and cached usernames
  // This forces re-render when geometry changes or usernames in cache are updated
  const geoJsonKey = geojson.features.map(f => {
    const baseKey = `${f.properties?.id || ''}_${f.properties?.color || ''}`;
    const cachedUsername = f.properties?.userId ? usernameCache.get(f.properties.userId) : '';
    const geometryHash = JSON.stringify(f.geometry);
    return `${baseKey}_${cachedUsername || ''}_${geometryHash}`;
  }).join('_');
  
  return (
    <GeoJSON
      key={geoJsonKey} // Re-render when geojson or cached usernames change
      data={geojson}
      style={style}
      onEachFeature={onEachFeature}
      pmIgnore={false} // Allow editing of synced layers
    />
  );
}

export default DrawnLayers;